import React from 'react';
import { useLabels, usePrefs } from 'wsm-common-data';
import { setClassNames } from 'ddc-classnames-js';
import { templatePropTypes } from '../../utilities/commonPropTypes';
import OptimizedImageBackground from '../../components/OptimizedImageBackground';
import AspectRatioBox from '../../components/aspect-ratio-box/AspectRatioBox';
import {
	ctaButtonFragment,
	detailsButtonFragment,
	offerDetails10x1Fragment
} from '../commonFragments';
import {
	getVehicleImages,
	getIncentiveImage,
	getNoImageLabels,
	getFormattedDollarSign
} from '../../utilities/general.jsx';
import { g } from '../../global-constants';
import './sass/Desktop10x1.scss';

const Desktop10x1 = ({
	applicableVehicle = '',
	carouselPosition,
	couponCode = '',
	ctaButtonData,
	description,

	discountDisplay = '',
	imageUrl,
	incentive,
	isMobile,
	isVisible,
	offerDetails,
	promo,
	title,
	type,
	vehicleImages = [],
	width
}) => {
	let template;
	const labels = useLabels();
	const {
		reduceDollarSignSize,
		vehicleBackgroundImage,
		modelOfferBackgroundImage
	} = usePrefs();
	const ctaButtonClasses = setClassNames(['btn-inverse']);
	const ctaModalButtonClasses = setClassNames(['my-4']);

	const detailsButtonClasses = [
		'pr-0',
		'text-decoration-underline',
		'text-transform-none'
	];

	const displayTextClasses = setClassNames([
		'd-flex',
		'flex-column',
		'justify-content-center',
		'flex-shrink-0'
	]);

	const displayClasses = setClassNames([
		'flex-1',
		'd-flex',
		'flex-row',
		'justify-content-center',
		'flex-shrink-0'
	]);

	const noImageLabels = getNoImageLabels(labels, promo.inventoryCount);

	if ([g.PARTS, g.SERVICE, g.EVENT].includes(type)) {
		template = (
			<div className="d-flex flex-row w-100 bg-neutral-1000">
				<OptimizedImageBackground
					key={type}
					className="w-100 flex-shrink-0 image"
					style={{
						backgroundImage: `url("${imageUrl}")`,
						backgroundPosition: 'center',
						backgroundSize: 'cover',
						backgroundColor: 'black',
						flex: 1
					}}
					width={width / 4}
				/>
				<div
					className={setClassNames([displayTextClasses, 'text'])}
					style={{
						flex: 2
					}}
				>
					<h2>
						{getFormattedDollarSign(title, reduceDollarSignSize)}
					</h2>
					<p>
						{getFormattedDollarSign(
							description,
							reduceDollarSignSize
						)}
					</p>
				</div>
				<div
					className={setClassNames([
						displayTextClasses,
						'text-center'
					])}
					style={{
						flex: 0.6
					}}
				>
					{discountDisplay && (
						<div className="font-weight-bold text-capitalize discount">
							{discountDisplay}
						</div>
					)}
				</div>
				<div
					className={setClassNames([
						displayTextClasses,
						'text-right',
						'align-items-end',
						'mr-3',
						'bg-neutral-1000'
					])}
					style={{
						flex: 1
					}}
				>
					{offerDetails10x1Fragment({
						couponCode,
						cta: ctaButtonFragment({
							carouselPosition,
							ctaButtonClasses,
							ctaButtonData,
							isVisible,
							promo
						}),
						details: detailsButtonFragment({
							carouselPosition,
							detailsButtonClasses,

							isVisible,
							promo,
							cta: ctaButtonFragment({
								carouselPosition,
								ctaModalButtonClasses,
								ctaButtonData,
								isVisible,
								promo
							})
						}),
						labels
					})}
				</div>
			</div>
		);
	}

	if (type && type === g.VEHICLE) {
		template = (
			<OptimizedImageBackground
				className="d-flex flex-row"
				style={{
					backgroundImage: `url("${vehicleBackgroundImage}")`,
					backgroundPosition: 'center 60%',
					backgroundSize: 'cover',
					flexBasis: `${width}px`
				}}
				width={width}
			>
				<div
					key={type}
					className={displayClasses}
					style={{
						flex: 1,
						alignItems: 'center',
						paddingLeft: '1%',
						paddingRight: '1%'
					}}
				>
					{getVehicleImages(vehicleImages, width / 2, isMobile, 0, {
						title: noImageLabels.title,
						subtitle: noImageLabels.subtitle,
						aspectRatio: 'desktop10x1'
					})}
				</div>
				<div
					className={setClassNames([
						displayTextClasses,
						'px-5',
						'bg-neutral-1000'
					])}
					style={{
						flex: 2
					}}
				>
					<h2>
						{getFormattedDollarSign(title, reduceDollarSignSize)}
					</h2>
					<p>
						{getFormattedDollarSign(
							description,
							reduceDollarSignSize
						)}
					</p>
				</div>
				<div
					className={setClassNames([
						displayTextClasses,
						'text-right',
						'align-items-end',
						'pr-3',
						'bg-neutral-1000'
					])}
					style={{
						flex: 1
					}}
				>
					{offerDetails10x1Fragment({
						couponCode,
						cta: ctaButtonFragment({
							carouselPosition,
							ctaButtonClasses,
							ctaButtonData,
							isVisible,
							promo
						}),
						details: detailsButtonFragment({
							carouselPosition,
							detailsButtonClasses,

							isVisible,
							promo,
							cta: ctaButtonFragment({
								carouselPosition,
								ctaModalButtonClasses,
								ctaButtonData,
								isVisible,
								promo
							})
						}),
						labels
					})}
				</div>
			</OptimizedImageBackground>
		);
	}

	if (type && type === g.INCENTIVE) {
		template = (
			<OptimizedImageBackground
				className="d-flex flex-row"
				style={{
					backgroundImage: `url("${modelOfferBackgroundImage}")`,
					backgroundPosition: 'bottom',
					backgroundSize: 'cover',
					flexBasis: `${width}px`
				}}
				width={width}
			>
				<div
					key={type}
					className={displayClasses}
					style={{
						flex: 1,
						alignItems: 'center'
					}}
				>
					{getIncentiveImage(
						imageUrl,
						width / 2,
						'', // leave empty unless we have a label other than YMMT
						isMobile,
						65,
						{
							title: noImageLabels.title,
							subtitle: noImageLabels.subtitle,
							aspectRatio: 'desktop10x1'
						}
					)}
				</div>
				<div
					className={setClassNames([
						displayTextClasses,
						'px-5',
						'bg-neutral-1000'
					])}
					style={{
						flex: 2
					}}
				>
					<h2>{applicableVehicle}</h2>
					<span className="pt-1 font-weight-normal w-100 d-block">
						{getFormattedDollarSign(title, reduceDollarSignSize)}
					</span>
					<p>
						{getFormattedDollarSign(
							offerDetails,
							reduceDollarSignSize
						)}
					</p>
				</div>
				<div
					className={setClassNames([
						displayTextClasses,
						'text-right',
						'align-items-end',
						'pr-3',
						'cta-wrapper',
						'bg-neutral-1000'
					])}
					style={{
						flex: 1
					}}
				>
					{ctaButtonFragment({
						carouselPosition,
						ctaButtonClasses,
						ctaButtonData,
						isVisible,
						promo
					})}
					{detailsButtonFragment({
						carouselPosition,
						detailsButtonClasses,

						incentive,
						isVisible,
						promo,
						cta: ctaButtonFragment({
							carouselPosition,
							ctaModalButtonClasses,
							ctaButtonData,
							isVisible,
							promo
						})
					})}
				</div>
			</OptimizedImageBackground>
		);
	}

	return (
		<AspectRatioBox>
			{template}
		</AspectRatioBox>
	);
};

Desktop10x1.propTypes = templatePropTypes;

export default Desktop10x1;
